import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField } from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIntroductions } from '../../../endpoints/introduction';
import { IntroductionMinimal } from '../../../types/introduction';
import ServerSideTable from '../../../Components/ServerSideTable';
import { SortOrder } from '../../../types/table';
import dayjs from 'dayjs';

const IntroductionList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof IntroductionMinimal>('first_name');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: introductionsData,
    isLoading,
    error,
  } = useApiGet(
    ['introductions', page, rowsPerPage, debouncedSearch, order, orderBy],
    () =>
      getIntroductions({
        page: page + 1, // API uses 1-based indexing
        per_page: rowsPerPage,
        search: debouncedSearch || undefined,
      })
  );

  const handleCreateIntroduction = () => {
    navigate('/introductions/create');
  };

  const handleViewIntroduction = (introduction: IntroductionMinimal) => {
    navigate(`/introductions/${introduction.id}`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof IntroductionMinimal) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const columns = [
    {
      id: 'first_name' as keyof IntroductionMinimal,
      label: 'Name',
      sortable: false,
      render: (value: any, introduction: IntroductionMinimal) =>
        `${introduction.first_name} ${introduction.last_name}`,
    },
    {
      id: 'country' as keyof IntroductionMinimal,
      label: 'Location',
      sortable: false,
      render: (value: any, introduction: IntroductionMinimal) =>
        `${introduction.country}${introduction.state ? `, ${introduction.state}` : ''}`,
    },
    {
      id: 'created_at' as keyof IntroductionMinimal,
      label: 'Submitted',
      sortable: false,
      render: (value: any, introduction: IntroductionMinimal) =>
        introduction.created_at
          ? dayjs(introduction.created_at).format('MM/DD/YYYY')
          : 'N/A',
    },
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading introductions: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Non-Member Intro
        </Typography>
        <Typography variant="subtitle1" component="p" color="text.secondary">
          Manage non-member introductions to other chapters. Create new
          introductions or view existing ones.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateIntroduction}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            Create Non-Member Intro
          </Button>
        </Box>
      </Box>

      {/* Search and Table */}
      <Paper sx={{ p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search introductions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
          }}
        />
        <ServerSideTable
          data={introductionsData?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={introductionsData?.pagination?.total_count || 0}
          totalPages={introductionsData?.pagination?.total_pages || 0}
          hasNext={introductionsData?.pagination?.has_next || false}
          hasPrev={introductionsData?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleViewIntroduction}
          emptyMessage="No introductions found. Create your first introduction to get started."
        />
      </Paper>
    </Box>
  );
};

export default IntroductionList;
